/**
 * VEYa — Root Index
 * Shows loading then redirects based on onboarding state.
 * Uses proper Zustand hydration callback and InteractionManager for reliable navigation.
 */
import React, { useEffect, useState, useRef } from 'react';
import { View, ActivityIndicator, StyleSheet, InteractionManager } from 'react-native';
import { router, useRootNavigationState } from 'expo-router';
import { useOnboardingStore } from '../src/stores/onboardingStore';

export default function Index() {
  const [isHydrated, setIsHydrated] = useState(useOnboardingStore.persist.hasHydrated());
  const hasNavigated = useRef(false);
  
  // Check if the navigation state is ready
  const rootNavigationState = useRootNavigationState();
  const isNavigationReady = rootNavigationState?.key != null;

  // Set up hydration listener using official Zustand callback
  useEffect(() => {
    // If already hydrated, no need to subscribe
    if (useOnboardingStore.persist.hasHydrated()) {
      setIsHydrated(true);
      return;
    }

    // Use the official onFinishHydration callback
    const unsubscribe = useOnboardingStore.persist.onFinishHydration(() => {
      console.log('[VEYa] Store hydration complete via callback');
      setIsHydrated(true);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Perform navigation once hydrated AND navigation is ready
  useEffect(() => {
    if (!isHydrated || !isNavigationReady || hasNavigated.current) {
      return;
    }

    // Use InteractionManager to ensure layout is fully mounted
    const interactionHandle = InteractionManager.runAfterInteractions(() => {
      if (hasNavigated.current) return;
      hasNavigated.current = true;

      const data = useOnboardingStore.getState().data;
      const isOnboarded = !!(
        data.name && 
        data.birthDate && 
        data.focusAreas && 
        data.focusAreas.length > 0
      );

      console.log('[VEYa] Navigating. isOnboarded:', isOnboarded, 'name:', data.name, 'areas:', data.focusAreas?.length);

      // Small delay to ensure all React Native animations are complete
      setTimeout(() => {
        if (isOnboarded) {
          router.replace('/(tabs)');
        } else {
          router.replace('/(auth)/welcome');
        }
      }, 50);
    });

    return () => {
      interactionHandle.cancel();
    };
  }, [isHydrated, isNavigationReady]);

  return (
    <View style={styles.container}>
      <ActivityIndicator size="large" color="#8B5CF6" />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FDFBF7',
  },
});
